import axios from "axios";
import React, { useState, useEffect, useRef } from "react";
import { Modal } from "react-bootstrap";
import "./Captcha.css";
import { showToast } from "../utils/library";
import CryptoJS from "crypto-js";

const CaptchaModal = ({ setState, showCaptchaModal, handleCaptchaSubmit }) => {
  const [captchaLoaded, setCaptchaLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [positionY, setPositionY] = useState(0);
  const [positionX, setPositionX] = useState(0);
  const [sessionId, setSessionId] = useState("");
  const [puzzleX, setPuzzleX] = useState(0);
  const [sliderValue, setSliderValue] = useState(0);
  const [baseImage, setBaseImage] = useState("");
  const [dragPiece, setDragPiece] = useState("");
  const baseImageRef = useRef(null);
  const dragPieceRef = useRef(null);
  const sliderRef = useRef(null);

  useEffect(() => {
    if (showCaptchaModal && !captchaLoaded) {
      generateCaptcha();
      setCaptchaLoaded(true);
    }
    if (!showCaptchaModal) {
      setCaptchaLoaded(false);
    }
  }, [showCaptchaModal]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (sliderRef?.current?.value == 0) generateCaptcha();
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  const tolerance = 5;
  const shapes = ["star", "circle", "triangle", "diamond", "puzzle", "heart"];

  const drawSquare = (ctx, cx, cy, width, height) => {
    ctx.beginPath();
    ctx.rect(cx - width / 2, cy - height / 2, width, height);
    ctx.closePath();
  };

  const drawTriangle = (ctx, cx, cy, width, height) => {
    ctx.beginPath();
    ctx.moveTo(cx, cy - height / 2);
    ctx.lineTo(cx - width / 2, cy + height / 2);
    ctx.lineTo(cx + width / 2, cy + height / 2);
    ctx.closePath();
  };

  const drawDiamond = (ctx, cx, cy, width, height) => {
    ctx.beginPath();
    ctx.moveTo(cx, cy - height / 2);
    ctx.lineTo(cx - width / 2, cy);
    ctx.lineTo(cx, cy + height / 2);
    ctx.lineTo(cx + width / 2, cy);
    ctx.closePath();
  };

  const drawHeart = (ctx, centerX, centerY, size) => {
    const topCurveHeight = size * 0.3; // Increased for better scaling
    ctx.beginPath();
    // Move to bottom tip of the heart
    ctx.moveTo(centerX, centerY + size / 2);
    // Left curve
    ctx.bezierCurveTo(
      centerX - size,
      centerY - topCurveHeight, // Outer left
      centerX - size * 0.1,
      centerY - topCurveHeight, // Inner left
      centerX,
      centerY // Connect to bottom
    );
    // Right curve
    ctx.bezierCurveTo(
      centerX + size * 0.1,
      centerY - topCurveHeight, // Inner right
      centerX + size,
      centerY - topCurveHeight, // Outer right
      centerX,
      centerY + size / 2 // Connect back
    );
    ctx.closePath();
    ctx.fillStyle = "red"; // Heart color
    ctx.fill();
    ctx.stroke();
  };

  const drawStar = (ctx, cx, cy, spikes, outerRadius, innerRadius) => {
    let rot = (Math.PI / 2) * 3;
    let x = cx;
    let y = cy;
    const step = Math.PI / spikes;
    ctx.beginPath();
    ctx.moveTo(cx, cy - outerRadius);
    for (let i = 0; i < spikes; i++) {
      x = cx + Math.cos(rot) * outerRadius;
      y = cy + Math.sin(rot) * outerRadius;
      ctx.lineTo(x, y);
      rot += step;
      x = cx + Math.cos(rot) * innerRadius;
      y = cy + Math.sin(rot) * innerRadius;
      ctx.lineTo(x, y);
      rot += step;
    }
    ctx.closePath();
  };

  const drawCircle = (ctx, cx, cy, radius) => {
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    ctx.closePath();
  };
  // Draw a puzzle shape
  const drawPuzzle = (ctx, centerX, centerY, size) => {
    const puzzleSize = size * 0.6;
    const tabSize = puzzleSize * 0.25; // Used for semicircle radius
    const radius = tabSize;
    const left = centerX - puzzleSize / 2;
    const right = centerX + puzzleSize / 2;
    const top = centerY - puzzleSize / 2;
    const bottom = centerY + puzzleSize / 2;
    const midX = centerX;
    const midY = centerY;
    const curveRadius = tabSize;
    ctx.beginPath();
    ctx.moveTo(left, top);
    // Top edge with interlocking tab
    ctx.lineTo(midX - tabSize, top);
    ctx.quadraticCurveTo(
      midX - curveRadius + 9,
      top - tabSize - 4,
      midX + tabSize,
      top
    );
    ctx.lineTo(right, top);
    // Right edge with interlocking tab
    ctx.lineTo(right, midY - tabSize);
    ctx.quadraticCurveTo(
      right + tabSize + 4,
      midY - curveRadius + 9,
      right,
      midY + tabSize
    );
    ctx.lineTo(right, bottom);
    // Bottom edge
    ctx.lineTo(left, bottom);
    // Left edge with semicircle cutout
    ctx.lineTo(left, midY + radius);
    ctx.quadraticCurveTo(left + radius, midY + radius, left + radius, midY);
    ctx.quadraticCurveTo(left + radius, midY - radius, left, midY - radius);
    ctx.lineTo(left, top);
    ctx.closePath();
    ctx.stroke();
  };

  const generateCaptcha = () => {
    setIsLoading(true);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    canvas.width = 300;
    canvas.height = 200;

    // Fetch the base64 image from the API
    axios
      .get(
        "https://captcha.paybito.com:7443/CaptchaService/captcha/generateCaptcha/200/300/50"
      ) // Replace with your actual API URL
      .then((response) => {
        console.log("response-->", response);
        setPositionX(response?.data?.positionX);
        setPositionY(response?.data?.positionY);
        setSessionId(response?.data?.sessionId);

        const base64Image = response?.data?.baseImage;
        const image = new Image();
        image.crossOrigin = "anonymous";
        image.src = base64Image;

        image.onload = () => {
          ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
          const randomX = Math.floor(Math.random() * (canvas.width - 50));
          setPuzzleX(randomX);
          const fixedY = Number(response?.data?.positionY);
          const shape = shapes[Math.floor(Math.random() * shapes.length)];
          const pieceCanvas = document.createElement("canvas");
          const pieceCtx = pieceCanvas.getContext("2d");
          pieceCanvas.width = 50;
          pieceCanvas.height = 50;

          // Draw the puzzle piece based on the shape
          if (shape === "star") {
            drawStar(pieceCtx, 25, 25, 5, 20, 10);
          } else if (shape === "circle") {
            drawCircle(pieceCtx, 25, 25, 20);
          } else if (shape === "square") {
            drawSquare(pieceCtx, 25, 25, 40, 40);
          } else if (shape === "triangle") {
            drawTriangle(pieceCtx, 25, 25, 40, 40);
          } else if (shape === "diamond") {
            drawDiamond(pieceCtx, 25, 25, 40, 40);
          } else if (shape === "heart") {
            drawHeart(pieceCtx, 25, 25, 50);
          } else if (shape === "puzzle") {
            drawPuzzle(pieceCtx, 25, 25, 60);
          }

          pieceCtx.clip();
          pieceCtx.drawImage(canvas, randomX, fixedY, 50, 50, 0, 0, 50, 50);
          ctx.save();
          ctx.beginPath();

          if (shape === "star") {
            drawStar(ctx, randomX + 25, fixedY + 25, 5, 20, 10);
          } else if (shape === "heart") {
            drawHeart(ctx, randomX + 25, fixedY + 25, 50);
          } else if (shape === "circle") {
            drawCircle(ctx, randomX + 25, fixedY + 25, 20);
          } else if (shape === "square") {
            drawSquare(ctx, randomX + 25, fixedY + 25, 40, 40);
          } else if (shape === "triangle") {
            drawTriangle(ctx, randomX + 25, fixedY + 25, 40, 40);
          } else if (shape === "diamond") {
            drawDiamond(ctx, randomX + 25, fixedY + 25, 40, 40);
          } else if (shape === "puzzle") {
            drawPuzzle(ctx, randomX + 25, fixedY + 25, 60);
          }

          ctx.clip();
          ctx.clearRect(randomX, fixedY, 50, 50);
          ctx.restore();

          setBaseImage(canvas.toDataURL()); // Update the base image
          setDragPiece(pieceCanvas.toDataURL()); // Update the puzzle piece image
          setSliderValue(0);
          dragPieceRef.current.style.left = "0px";
          setIsLoading(false);
        };
      })
      .catch((error) => {
        console.error("Error fetching CAPTCHA image:", error);
        setIsLoading(false);
        showToast("error", "Failed to load CAPTCHA image.");
      });
  };
  const handleSliderChange = (e) => {
    const value = e.target.value;
    console.log("valueeee", value);

    setSliderValue(value);
    const maxOffset =
      baseImageRef.current.offsetWidth - dragPieceRef.current.offsetWidth;
    dragPieceRef.current.style.left = `${(value / 100) * maxOffset}px`;
  };

  const handleSliderValidation = () => {
    let CAPTCHA_SECRET = "Rtz2IQzefccFwmiurXq)8IjzUm?v3o";
    const maxOffset =
      baseImageRef.current.offsetWidth - dragPieceRef.current.offsetWidth;
    const offsetX = (sliderValue / 100) * maxOffset;
    if (Math.abs(offsetX - puzzleX) <= tolerance) {
      const input = `${Number(positionX)}:${CAPTCHA_SECRET}:${Number(
        positionY
      )}`;
      console.log("input", input);
      const hash = CryptoJS.SHA256(input).toString(CryptoJS.enc.Hex);
      setState({ gRecaptchaResponse: hash });
      setState({ showCaptchaModal: false });
      setState({ showCaptchaModalSecond: false });
      setState({ showCaptchaModalThird: false });
      setState({ showCaptchaModalDelete: false });
      handleCaptchaSubmit(hash, sessionId);
    } else {
      showToast("error", "Incorrect CAPTCHA");
      generateCaptcha();
    }
  };

  const handleCaptchaModalClose = () => {
    setState({ showCaptchaModal: false });
    setState({ showCaptchaModalSecond: false });
    setState({ showCaptchaModalThird: false });
    setState({ showCaptchaModalDelete: false });
  };

  return (
    <Modal
      show={showCaptchaModal}
      backdrop="static"
      keyboard={false}
      contentClassName="captcha-modal-content"
      dialogClassName="captcha-modal-dialog"
    >
      <Modal.Header className="d-flex justify-content-between">
        <h3 />
        <h3 style={{ fontWeight: "bolder", color: "#000" }}>Verify</h3>
        <i
          className="fa-solid fa-xmark"
          style={{ cursor: "pointer", fontSize: "20px", color: "#000" }}
          onClick={handleCaptchaModalClose}
        ></i>
      </Modal.Header>

      <Modal.Body>
        <div className="captcha-container">
          <h3 style={{ fontWeight: "bolder", color: "#000" }}>
            Slide to fit the puzzle piece
          </h3>
          <div className="captcha-area">
            <img ref={baseImageRef} src={baseImage} alt="Base Image" />
            <img
              ref={dragPieceRef}
              src={dragPiece}
              alt="Puzzle Piece"
              style={{
                position: "absolute",
                top: `${positionY}px`,
                left: "0px",
                width: "50px",
                height: "50px",
                cursor: "grab",
                zIndex: 10,
                transition: "left 0.1s ease-in-out",
              }}
            />
            {isLoading && (
              <div className="loading style-2">
                <div className="loading-wheel"></div>
              </div>
            )}
          </div>
          <input
            ref={sliderRef}
            type="range"
            min="0"
            max="100"
            value={sliderValue}
            className="slider"
            onChange={handleSliderChange}
            onMouseUp={handleSliderValidation}
            onTouchEnd={handleSliderValidation}
          />
          <div className="d-flex">
            <i
              className="fa-solid fa-arrows-rotate"
              style={styles.icons}
              onClick={generateCaptcha}
            ></i>

            <i
              className="fa-regular fa-circle-xmark icons"
              style={styles.icons}
              onClick={handleCaptchaModalClose}
            ></i>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

const styles = {
  captchaContainer: {
    textAlign: "center",
    margin: "20px auto",
    width: "320px",
    padding: "15px",
    border: "1px solid #ccc",
    borderRadius: "10px",
    background: "linear-gradient(145deg, #FFFFFF, #F0F0F0)",
    boxShadow:
      "5px 5px 15px rgba(0, 0, 0, 0.1), -5px -5px 15px rgba(255, 255, 255, 0.7)",
  },
  captchaArea: {
    position: "relative",
    width: "300px",
    height: "200px",
    margin: "10px auto",
    border: "2px solid #ddd",
    overflow: "hidden",
    borderRadius: "8px",
    backgroundColor: "#eef",
    boxShadow: "inset 3px 3px 8px rgba(0, 0, 0, 0.1)",
  },
  baseImage: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
  },
  dragPiece: {
    position: "absolute",
    top: "90px",
    width: "50px",
    height: "50px",
    cursor: "grab",
    zIndex: 10,
    transition: "left 0.1s ease-in-out",
  },
  slider: {
    width: "100%",
    marginTop: "15px",
    appearance: "none",
    height: "6px",
    background: "#ddd",
    outline: "none",
    borderRadius: "5px",
  },
  icons: {
    color: "gray",
    margin: "10px",
    fontSize: "25px",
    cursor: "pointer",
  },
};

export default CaptchaModal;
